import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import { AGE_OPTIONS } from '@/pages/Onboarding/options/Age/AGE_OPTIONS';
import { AgeOptionIdType } from '@/pages/Onboarding/options/Age/types';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { LayoutDataType } from '../../types';
import { ETHNICITY_OPTIONS } from '../Ethnicity/constants';

import { ScreenAgeStyles } from './Age.styles';

const preloadArray = AGE_OPTIONS.reduce<string[]>((acc, ageOption) => {
  const ethnicityImages = ETHNICITY_OPTIONS.map((ethnicityOption) => {
    if (webCampaign.getLayoutNotes().includes('three-screens-new')) {
      return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/ethnicity/sprite-${ageOption.id}-${ethnicityOption.id}.png`;
    }

    return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/ethnicity/${ethnicityOption.id}/sprite-${ageOption.id}.png`;
  });

  acc = [...acc, ...ethnicityImages];

  return acc;
}, []);

export const ScreenAge: React.FC = () => {
  const { user } = useAccount();

  React.useEffect(() => {
    if (user && canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Age',
      });
    }
    preloadArray.forEach((src) => {
      preloadImage(src);
    });
  }, [user]);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const age = form.watch('age');

  const handleSelect = (option: OnbOptionType<LayoutDataType['age']>) => {
    form.setValue('age', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        age: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const getImageSrc = (ageId: OnbOptionType<AgeOptionIdType>['id']) => {
    if (webCampaign.getLayoutNotes().includes('three-screens-new')) {
      return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/age/sprite-${ageId}.png`;
    }

    return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/age/sprite-${ageId}.png`;
  };

  return (
    <ScreenAgeStyles>
      <OnboardingNavigation />
      <OwnAiTitle>
        Choose your AI partner's <OwnAiTitleHighlight>age</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {AGE_OPTIONS.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="age"
              isSelected={age === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={getImageSrc(option.id)}
              title={option.title}
              TEST_ID={`age-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>
    </ScreenAgeStyles>
  );
};
